import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense, Component } from 'react';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { onCLS, onINP, onLCP } from 'web-vitals';

import { AuthProvider }        from './context/AuthContext';
import { AdminAuthProvider }   from './context/AdminAuthContext';
import { CompanyAuthProvider } from './context/CompanyAuthContext';
import { ThemeProvider }       from './context/ThemeContext';
import { ProtectedRoute, AdminProtectedRoute } from './components/common/ProtectedRoute';
import AdminLayout   from './components/admin/AdminLayout';
import UserLayout    from './components/common/UserLayout';
import CompanyLayout, { CompanyProtectedRoute } from './components/company/CompanyLayout';
import AnnouncementBanner  from './components/common/AnnouncementBanner';
import ImpersonationBanner from './components/common/ImpersonationBanner';

// ── Eager-load only the shell (layouts, contexts, routing) ───────────────────
// All page components are lazy-loaded for code splitting
const LoginPage        = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage     = lazy(() => import('./pages/auth/RegisterPage'));
const HomePage         = lazy(() => import('./pages/public/HomePage'));
const BrowseRolesPage  = lazy(() => import('./pages/public/BrowseRolesPage'));
const TrialPage        = lazy(() => import('./pages/public/TrialPage'));
const PricingPage      = lazy(() => import('./pages/public/PricingPage'));
const RoleOverviewPage = lazy(() => import('./pages/dashboard/RoleOverviewPage'));
const DashboardPage    = lazy(() => import('./pages/dashboard/DashboardPage'));
const QuizSessionPage  = lazy(() => import('./pages/dashboard/QuizSessionPage'));
const StudyChapterPage = lazy(() => import('./pages/dashboard/StudyChapterPage'));
const FlashCardPage    = lazy(() => import('./pages/dashboard/FlashCardPage'));

// ── Sentry init (uses env var — no-op if not set) ─────────────────────────────
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn:                process.env.REACT_APP_SENTRY_DSN,
    environment:        process.env.NODE_ENV,
    tracesSampleRate:   process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// Report Web Vitals to analytics
function reportWebVitals() {
  const send = ({ name, value }) => {
    if (process.env.NODE_ENV !== 'production') return;
    if (window.analytics?.track) window.analytics.track('Web Vital', { name, value: Math.round(value) });
  };
  onCLS(send); onINP(send); onLCP(send);
}

// ── Lazy-load heavy / infrequent pages ────────────────────────────────────────
const ResultsPage       = lazy(() => import('./pages/dashboard/ResultsPage'));
const SettingsPage      = lazy(() => import('./pages/dashboard/SettingsPage'));
const CertificatesPage  = lazy(() => import('./pages/dashboard/CertificatesPage'));
const JDPrepPage        = lazy(() => import('./pages/dashboard/JDPrepPage'));
const JDPrepResultsPage = lazy(() => import('./pages/dashboard/JDPrepResultsPage'));
const JDPrepQuizPage    = lazy(() => import('./pages/dashboard/JDPrepQuizPage'));
const DrillPage         = lazy(() => import('./pages/dashboard/DrillPage'));
const SubscribePage     = lazy(() => import('./pages/public/SubscribePage'));

const JDPrepLandingPage  = lazy(() => import('./pages/public/JDPrepLandingPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage    = lazy(() => import('./pages/auth/VerifyEmailPage'));

const VerifyPage  = lazy(() => import('./pages/public/VerifyPage'));

// Trust / legal pages
const AboutPage   = lazy(() => import('./pages/public/AboutPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const TermsPage   = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));

// Blog
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage'));
const BlogPostPage  = lazy(() => import('./pages/blog/BlogPostPage'));

// Admin — large bundle, lazy-loaded only when user visits /admin/*
const AdminLoginPage   = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard   = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminRoles       = lazy(() => import('./pages/admin/AdminRoles'));
const AdminChapters    = lazy(() => import('./pages/admin/AdminChapters'));
const AdminSubChapters = lazy(() => import('./pages/admin/AdminSubChapters'));
const AdminQuestions   = lazy(() => import('./pages/admin/AdminQuestions'));
const AdminUsers       = lazy(() => import('./pages/admin/AdminUsers'));
const AdminBulkUpload  = lazy(() => import('./pages/admin/AdminBulkUpload'));
const AdminRefunds     = lazy(() => import('./pages/admin/AdminRefunds'));
const AdminExport      = lazy(() => import('./pages/admin/AdminExport'));
const AdminActivityLog = lazy(() => import('./pages/admin/AdminActivityLog'));
const AdminContentHealth = lazy(() => import('./pages/admin/AdminContentHealth'));
const AdminReviewQueue = lazy(() => import('./pages/admin/AdminReviewQueue'));
const AdminCoupons     = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminBroadcast   = lazy(() => import('./pages/admin/AdminBroadcast'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions').then(m => ({ default: m.AdminSubscriptions })));
const AdminPayments      = lazy(() => import('./pages/admin/AdminSubscriptions').then(m => ({ default: m.AdminPayments })));
const AdminUserAnalytics      = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminUserAnalytics })));
const AdminRevenueAnalytics   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminRevenueAnalytics })));
const AdminContentAnalytics   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminContentAnalytics })));
const AdminConversionFunnel   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminConversionFunnel })));
const AdminTrialAnalytics     = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminTrialAnalytics })));
const AdminCertificates = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminCertificates })));
const AdminAdmins       = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminAdmins })));
const AdminSettings     = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminSettings })));
const AdminJDPrep       = lazy(() => import('./pages/admin/AdminJDPrep'));
const AdminDocsPage     = lazy(() => import('./pages/admin/AdminDocsPage'));
const AdminStudyContent    = lazy(() => import('./pages/admin/AdminStudyContent'));
const AdminExplainContent  = lazy(() => import('./pages/admin/AdminExplainContent'));
const AdminSupport      = lazy(() => import('./pages/admin/AdminSupport'));
const AdminBlog         = lazy(() => import('./pages/admin/AdminBlog'));
const AdminCarousel     = lazy(() => import('./pages/admin/AdminCarousel'));
const AdminCourses         = lazy(() => import('./pages/admin/AdminCourses'));
const AdminCourseGenerate  = lazy(() => import('./pages/admin/AdminCourseGenerate'));
const AdminCourseDetail    = lazy(() => import('./pages/admin/AdminCourseDetail'));
const AdminEmailTemplates  = lazy(() => import('./pages/admin/AdminEmailTemplates'));

// Features page
const FeaturesPage      = lazy(() => import('./pages/public/FeaturesPage'));
const ComparisonPage    = lazy(() => import('./pages/public/ComparisonPage'));

// Public tools / resources / landing pages
const QuestionGeneratorPage = lazy(() => import('./pages/public/QuestionGeneratorPage'));
const StarMethodPage        = lazy(() => import('./pages/public/StarMethodPage'));
const PressPage             = lazy(() => import('./pages/public/PressPage'));
const StudentsPage          = lazy(() => import('./pages/public/StudentsPage'));

// User-facing course pages
const CoursesPage      = lazy(() => import('./pages/public/CoursesPage'));
const CoursePage       = lazy(() => import('./pages/dashboard/CoursePage'));
const CourseTopicPage  = lazy(() => import('./pages/dashboard/CourseTopicPage'));
const CourseReaderPage = lazy(() => import('./pages/dashboard/CourseReaderPage'));
const WalletPage          = lazy(() => import('./pages/dashboard/WalletPage'));
const ReferPage           = lazy(() => import('./pages/dashboard/ReferPage'));
const ATSPage             = lazy(() => import('./pages/dashboard/ATSPage'));
const MockInterviewPage   = lazy(() => import('./pages/dashboard/MockInterviewPage'));
const OutcomePage         = lazy(() => import('./pages/dashboard/OutcomePage'));
const MyInterviewsPage    = lazy(() => import('./pages/dashboard/MyInterviewsPage'));
const PerformancePage     = lazy(() => import('./pages/dashboard/PerformancePage'));
const AdminVouchers    = lazy(() => import('./pages/admin/AdminVouchers'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminCompanyReviews = lazy(() => import('./pages/admin/AdminCompanyReviews'));

// Public careers pages (catch-all — must stay after all specific routes)
const CareersIndexPage   = lazy(() => import('./pages/public/CareersIndexPage'));
const CompanyCareersPage = lazy(() => import('./pages/public/CompanyCareersPage'));
const CompanyJobPage     = lazy(() => import('./pages/public/CompanyJobPage'));

// Company portal
const CompanyLoginPage      = lazy(() => import('./pages/company/CompanyLoginPage'));
const CompanyRegisterPage   = lazy(() => import('./pages/company/CompanyRegisterPage'));
const CompanyActivatePage   = lazy(() => import('./pages/company/CompanyActivatePage'));
const CompanyPricingPage    = lazy(() => import('./pages/company/CompanyPricingPage'));
const CompanyDashboard      = lazy(() => import('./pages/company/CompanyDashboard'));
const AssessmentsListPage   = lazy(() => import('./pages/company/AssessmentsListPage'));
const AssessmentCreatePage  = lazy(() => import('./pages/company/AssessmentCreatePage'));
const AssessmentDetailPage  = lazy(() => import('./pages/company/AssessmentDetailPage'));
const CandidatesPage        = lazy(() => import('./pages/company/CandidatesPage'));
const TeamPage              = lazy(() => import('./pages/company/TeamPage'));
const BillingPage           = lazy(() => import('./pages/company/BillingPage'));
const CreditsPage           = lazy(() => import('./pages/company/CreditsPage'));
const CompanyProfilePage    = lazy(() => import('./pages/company/CompanyProfilePage'));
const RecruitPage           = lazy(() => import('./pages/company/RecruitPage'));
const PostJobPage           = lazy(() => import('./pages/company/PostJobPage'));
const JobDetailPage         = lazy(() => import('./pages/company/JobDetailPage'));
const CompanyHelpPage       = lazy(() => import('./pages/company/CompanyHelpPage'));
const JobApplyPage          = lazy(() => import('./pages/public/JobApplyPage'));
const ApplicationTrackPage  = lazy(() => import('./pages/public/ApplicationTrackPage'));
const CVScanPage            = lazy(() => import('./pages/company/CVScanPage'));
const TalentPoolPage        = lazy(() => import('./pages/company/TalentPoolPage'));
const AnalyticsDashboardPage    = lazy(() => import('./pages/company/AnalyticsDashboardPage'));
const AuditLogPage              = lazy(() => import('./pages/company/AuditLogPage'));
const DepartmentsPage           = lazy(() => import('./pages/company/DepartmentsPage'));
const HiringRequestsPage        = lazy(() => import('./pages/company/HiringRequestsPage'));
const EmailTemplatesPage        = lazy(() => import('./pages/company/EmailTemplatesPage'));
const AutomationPage            = lazy(() => import('./pages/company/AutomationPage'));
const PipelineTemplatesPage     = lazy(() => import('./pages/company/PipelineTemplatesPage'));
const WebhooksPage              = lazy(() => import('./pages/company/WebhooksPage'));
const TalentCommunityAdminPage  = lazy(() => import('./pages/company/TalentCommunityAdminPage'));
const GdprPage                  = lazy(() => import('./pages/company/GdprPage'));
const BrandAnalyticsPage        = lazy(() => import('./pages/company/BrandAnalyticsPage'));
const SlaPage                   = lazy(() => import('./pages/company/SlaPage'));
const DiversityPage             = lazy(() => import('./pages/company/DiversityPage'));
const WidgetPage                = lazy(() => import('./pages/company/WidgetPage'));
const ReEngagePage              = lazy(() => import('./pages/company/ReEngagePage'));
const InternalJobsPage          = lazy(() => import('./pages/public/InternalJobsPage'));
const TalentCommunityPage       = lazy(() => import('./pages/public/TalentCommunityPage'));
const NpsPage                   = lazy(() => import('./pages/public/NpsPage'));
const InterviewSchedulePage     = lazy(() => import('./pages/public/InterviewSchedulePage'));
const CandidatePortalPage       = lazy(() => import('./pages/public/CandidatePortalPage'));
const FeedbackReportPage        = lazy(() => import('./pages/public/FeedbackReportPage'));
const InterviewsPage            = lazy(() => import('./pages/company/InterviewsPage'));
const VideoInterviewRoom        = lazy(() => import('./pages/company/VideoInterviewRoom'));
const PostHirePage              = lazy(() => import('./pages/company/PostHirePage'));
const SkillsTestsPage          = lazy(() => import('./pages/company/SkillsTestsPage'));
const WhitelabelPage           = lazy(() => import('./pages/company/WhitelabelPage'));
const ReferencePage             = lazy(() => import('./pages/public/ReferencePage'));
const CandidateSessionPage      = lazy(() => import('./pages/company/CandidateSessionPage'));
const CandidateVideoRoom        = lazy(() => import('./pages/candidate/CandidateVideoRoom'));
const AdminCompanies        = lazy(() => import('./pages/admin/AdminCompanies'));
const AdminCompanyCoupons   = lazy(() => import('./pages/admin/AdminCompanyCoupons'));
const CandidateAssessmentPage = lazy(() => import('./pages/candidate/CandidateAssessmentPage'));
const JobReviewPage           = lazy(() => import('./pages/public/JobReviewPage'));

// ── New B2C AI tools ──────────────────────────────────────────────────────────
const StarCoachPage         = lazy(() => import('./pages/dashboard/StarCoachPage'));
const CoverLetterPage       = lazy(() => import('./pages/dashboard/CoverLetterPage'));
const CompanyPrepPage       = lazy(() => import('./pages/dashboard/CompanyPrepPage'));
const DebriefPage           = lazy(() => import('./pages/dashboard/DebriefPage'));
const LinkedInPage          = lazy(() => import('./pages/dashboard/LinkedInPage'));
const SalaryNegPage         = lazy(() => import('./pages/dashboard/SalaryNegPage'));
const ReadinessReportPage   = lazy(() => import('./pages/dashboard/ReadinessReportPage'));
const SharedReadinessReport = lazy(() => import('./pages/public/SharedReadinessReport'));

// ── Error boundary for lazy-loaded chunks ────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f4f0)', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#C1272D" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <p style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>Page failed to load</p>
          <p style={{ fontSize: 14, color: '#64748B', margin: '0 0 22px', lineHeight: 1.6 }}>This is usually a network issue. Check your connection and try again.</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '10px 24px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

// ── Fallback spinner ──────────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f4f0)' }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

const Placeholder = ({ name }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f4f0)' }}>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1B3A5C', margin: '0 0 8px' }}>{name}</h1>
      <p style={{ color: '#73726c', fontSize: 14 }}>Coming soon</p>
    </div>
  </div>
);

const Admin = ({ children }) => (
  <AdminProtectedRoute><AdminLayout>{children}</AdminLayout></AdminProtectedRoute>
);

const Shell = ({ children }) => (
  <ProtectedRoute><UserLayout>{children}</UserLayout></ProtectedRoute>
);

const Company = ({ children }) => (
  <CompanyProtectedRoute><CompanyLayout>{children}</CompanyLayout></CompanyProtectedRoute>
);

// ── Scroll to top on route change ─────────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ImpersonationHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => {
    const token = searchParams.get('impersonate');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.impersonated) {
          sessionStorage.setItem('ip_impersonate_token', token);
          setImpersonating({ name: payload.full_name || payload.email || 'User' });
          setSearchParams({}, { replace: true });
        }
      } catch {}
      return;
    }
    const stored = sessionStorage.getItem('ip_impersonate_token');
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        if (payload.impersonated) {
          if (Date.now() < payload.exp * 1000) setImpersonating({ name: payload.full_name || payload.email || 'User' });
          else sessionStorage.removeItem('ip_impersonate_token');
        }
      } catch {}
    }
  }, [searchParams, setSearchParams]);

  const exitImpersonation = () => { sessionStorage.removeItem('ip_impersonate_token'); setImpersonating(null); window.location.reload(); };

  if (!impersonating) return null;
  return <ImpersonationBanner userName={impersonating.name} onExit={exitImpersonation} />;
}

// react-helmet-async only manages tags it renders itself (marked data-rh="true")
// — it has no way to know that public/index.html's static <head> tags are meant
// to be replaced, so PageMeta's per-page canonical/description/OG/Twitter tags
// end up duplicated alongside the generic static ones instead of overriding
// them. Two conflicting <link rel="canonical"> tags on the same page is a
// documented cause of Search Console's "Duplicate without user-selected
// canonical" — confirmed live on this site 2026-07-05 via GSC's rendered-HTML
// output, which showed both the static (no trailing slash) and Helmet's
// (trailing slash) canonical simultaneously present.
//
// A MutationObserver (not a one-shot effect) is required here rather than a
// timed cleanup because every page component is lazy-loaded behind Suspense —
// it mounts, and its nested <Helmet> commits its tags, asynchronously after
// its JS chunk loads. A plain `useEffect(() => {...}, [])` at the App root
// would very likely run before Helmet has committed anything at all. This
// instead reacts to <head> mutations whenever they actually happen.
const TRACKED_META_SELECTORS = [
  'link[rel="canonical"]',
  'meta[name="description"]',
  ...['og:type', 'og:url', 'og:title', 'og:description', 'og:image', 'og:image:width', 'og:image:height', 'og:site_name', 'og:locale']
    .map((p) => `meta[property="${p}"]`),
  ...['twitter:card', 'twitter:url', 'twitter:site', 'twitter:title', 'twitter:description', 'twitter:image']
    .map((n) => `meta[name="${n}"]`),
];

// Same react-snap-baking root cause as the meta tags above, applied to
// GTM/Clarity/Google Sign-In: public/index.html injects each of these via
// document.createElement + appendChild (needed to conditionally skip them
// on private routes, or scope Google Sign-In to /login and /register only —
// see index.html for why), so react-snap's snapshot of a matching page
// captures BOTH the original inline injector script AND the tag it
// created. A real browser loading that prerendered page then re-runs the
// inline injector on top of the already-baked-in tag — two identical
// <script src> tags, two network requests, every visit. A guard inside the
// injector itself (document.querySelector before creating) does NOT fix
// this: the injector always executes before the browser's parser reaches
// the baked-in tag, which sits later in the same document — confirmed via
// a real bake-then-reload simulation in a real browser, 2026-07-09.
// Cleanup after the DOM has settled, same as the meta tags above, is what
// actually works.
const DUPLICATE_SCRIPT_SELECTORS = [
  'script[src*="googletagmanager.com/gtag/js"]',
  'script[src*="clarity.ms/tag/"]',
  'script[src*="accounts.google.com/gsi/client"]',
];

function useCleanupDuplicateMetaTags() {
  useEffect(() => {
    const cleanup = () => {
      TRACKED_META_SELECTORS.forEach((selector) => {
        // Only remove the static original once Helmet has actually rendered
        // its own replacement for this exact tag — never leave a page with
        // zero canonical/description/OG tags (needed for non-JS crawlers and
        // social-media unfurlers, which don't execute JS at all).
        const helmetManaged = document.head.querySelector(`${selector}[data-rh]`);
        if (!helmetManaged) return;
        const stale = document.head.querySelector(`${selector}:not([data-rh])`);
        if (stale) stale.remove();
      });

      DUPLICATE_SCRIPT_SELECTORS.forEach((selector) => {
        const matches = document.head.querySelectorAll(selector);
        for (let i = 1; i < matches.length; i++) matches[i].remove();
      });
    };

    cleanup(); // covers the case where Helmet already committed before this attached
    const observer = new MutationObserver(cleanup);
    observer.observe(document.head, { childList: true });
    return () => observer.disconnect();
  }, []);
}

export default function App() {
  useEffect(() => { reportWebVitals(); }, []);
  useCleanupDuplicateMetaTags();

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AdminAuthProvider>
          <CompanyAuthProvider>
            {/* Accessibility: skip navigation */}
            <a href="#main-content" className="skip-link">Skip to main content</a>

            <AnnouncementBanner />
            <ImpersonationHandler />
            <ScrollToTop />
            <Toaster position="top-right" toastOptions={{ duration: 4000, style: { fontSize: '14px', borderRadius: '10px', background: 'var(--bg-elevated, #fff)', color: 'var(--text, #1a1a18)' } }} />

            <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public */}
                <Route path="/"         element={<HomePage />} />
                <Route path="/roles"    element={<BrowseRolesPage />} />
                <Route path="/trial"    element={<TrialPage />} />
                <Route path="/trial/:roleSlug" element={<TrialPage />} />
                <Route path="/pricing"  element={<PricingPage />} />
                <Route path="/interview-prep" element={<JDPrepLandingPage />} />
                <Route path="/about"    element={<AboutPage />} />
                <Route path="/contact"  element={<ContactPage />} />
                <Route path="/terms"    element={<TermsPage />} />
                <Route path="/privacy"  element={<PrivacyPage />} />
                <Route path="/blog"     element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/verify"       element={<VerifyPage />} />
                <Route path="/verify/:code" element={<VerifyPage />} />
                <Route path="/login"    element={<LoginPage />} />
                <Route path="/register"           element={<RegisterPage />} />
                <Route path="/forgot-password"    element={<ForgotPasswordPage />} />
                <Route path="/reset-password"     element={<ResetPasswordPage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                <Route path="/roles/:slug"         element={<RoleOverviewPage />} />
                <Route path="/subscribe/:slug"     element={<SubscribePage />} />
                <Route path="/features"            element={<FeaturesPage />} />
                <Route path="/courses"             element={<CoursesPage />} />
                <Route path="/vs/:competitor"           element={<ComparisonPage />} />
                <Route path="/tools/interview-questions" element={<QuestionGeneratorPage />} />
                <Route path="/resources/star-method"     element={<StarMethodPage />} />
                <Route path="/press"                     element={<PressPage />} />
                <Route path="/students"                  element={<StudentsPage />} />

                {/* Protected user */}
                <Route path="/dashboard"             element={<Shell><main id="main-content"><DashboardPage /></main></Shell>} />
                <Route path="/study/:subChapterId"        element={<ProtectedRoute><StudyChapterPage /></ProtectedRoute>} />
                <Route path="/flashcards/:subChapterId"  element={<ProtectedRoute><FlashCardPage /></ProtectedRoute>} />
                <Route path="/quiz/:subChapterId"    element={<ProtectedRoute><QuizSessionPage /></ProtectedRoute>} />
                <Route path="/drill/:roleId"         element={<ProtectedRoute><DrillPage /></ProtectedRoute>} />
                <Route path="/results/:progressId"   element={<Shell><main id="main-content"><ResultsPage /></main></Shell>} />
                <Route path="/settings"              element={<Shell><main id="main-content"><SettingsPage /></main></Shell>} />
                <Route path="/certificates"          element={<Shell><main id="main-content"><CertificatesPage /></main></Shell>} />
                <Route path="/jd-prep"               element={<Shell><main id="main-content"><JDPrepPage /></main></Shell>} />
                <Route path="/jd-prep/:id"           element={<Shell><main id="main-content"><JDPrepResultsPage /></main></Shell>} />
                <Route path="/jd-prep/:id/quiz"     element={<ProtectedRoute><JDPrepQuizPage /></ProtectedRoute>} />
                <Route path="/courses/:slug"             element={<CoursePage />} />
                <Route path="/courses/topic/:topicId"    element={<CourseTopicPage />} />
                <Route path="/courses/learn/:courseId"   element={<ProtectedRoute><CourseReaderPage /></ProtectedRoute>} />
                <Route path="/courses/:courseSlug/:topicSlug" element={<CourseTopicPage />} />
                <Route path="/wallet"                    element={<Shell><main id="main-content"><WalletPage /></main></Shell>} />
                <Route path="/refer"                     element={<Shell><main id="main-content"><ReferPage /></main></Shell>} />
                <Route path="/ats"                       element={<Shell><main id="main-content"><ATSPage /></main></Shell>} />
                <Route path="/my-interviews"             element={<Shell><main id="main-content"><MyInterviewsPage /></main></Shell>} />
                <Route path="/outcome/:roleId"           element={<OutcomePage />} />
                <Route path="/mock-interview"            element={<Shell><main id="main-content"><MockInterviewPage /></main></Shell>} />
                <Route path="/performance"               element={<Shell><main id="main-content"><PerformancePage /></main></Shell>} />
                <Route path="/star-coach"               element={<Shell><main id="main-content"><StarCoachPage /></main></Shell>} />
                <Route path="/cover-letter"             element={<Shell><main id="main-content"><CoverLetterPage /></main></Shell>} />
                <Route path="/company-prep"             element={<Shell><main id="main-content"><CompanyPrepPage /></main></Shell>} />
                <Route path="/debrief"                  element={<Shell><main id="main-content"><DebriefPage /></main></Shell>} />
                <Route path="/linkedin-opt"             element={<Shell><main id="main-content"><LinkedInPage /></main></Shell>} />
                <Route path="/salary-neg"               element={<Shell><main id="main-content"><SalaryNegPage /></main></Shell>} />
                <Route path="/readiness-report"         element={<Shell><main id="main-content"><ReadinessReportPage /></main></Shell>} />
                <Route path="/readiness/:token"         element={<SharedReadinessReport />} />

                {/* Admin */}
                <Route path="/admin/login"            element={<AdminLoginPage />} />
                <Route path="/admin"                  element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard"        element={<Admin><AdminDashboard /></Admin>} />
                <Route path="/admin/roles"            element={<Admin><AdminRoles /></Admin>} />
                <Route path="/admin/chapters"         element={<Admin><AdminChapters /></Admin>} />
                <Route path="/admin/sub-chapters"     element={<Admin><AdminSubChapters /></Admin>} />
                <Route path="/admin/questions"        element={<Admin><AdminQuestions /></Admin>} />
                <Route path="/admin/users"            element={<Admin><AdminUsers /></Admin>} />
                <Route path="/admin/subscriptions"    element={<Admin><AdminSubscriptions /></Admin>} />
                <Route path="/admin/payments"         element={<Admin><AdminPayments /></Admin>} />
                <Route path="/admin/refunds"          element={<Admin><AdminRefunds /></Admin>} />
                <Route path="/admin/analytics/users"  element={<Admin><AdminUserAnalytics /></Admin>} />
                <Route path="/admin/analytics/revenue" element={<Admin><AdminRevenueAnalytics /></Admin>} />
                <Route path="/admin/analytics/content" element={<Admin><AdminContentAnalytics /></Admin>} />
                <Route path="/admin/analytics/conversion" element={<Admin><AdminConversionFunnel /></Admin>} />
                <Route path="/admin/analytics/trial"      element={<Admin><AdminTrialAnalytics /></Admin>} />
                <Route path="/admin/certificates"     element={<Admin><AdminCertificates /></Admin>} />
                <Route path="/admin/admins"           element={<Admin><AdminAdmins /></Admin>} />
                <Route path="/admin/bulk-upload"      element={<Admin><AdminBulkUpload /></Admin>} />
                <Route path="/admin/settings"         element={<Admin><AdminSettings /></Admin>} />
                <Route path="/admin/activity-log"     element={<Admin><AdminActivityLog /></Admin>} />
                <Route path="/admin/content-health"   element={<Admin><AdminContentHealth /></Admin>} />
                <Route path="/admin/review-queue"     element={<Admin><AdminReviewQueue /></Admin>} />
                <Route path="/admin/coupons"          element={<Admin><AdminCoupons /></Admin>} />
                <Route path="/admin/broadcast"        element={<Admin><AdminBroadcast /></Admin>} />
                <Route path="/admin/email-templates"  element={<Admin><AdminEmailTemplates /></Admin>} />
                <Route path="/admin/export"           element={<Admin><AdminExport /></Admin>} />
                <Route path="/admin/jd-prep"          element={<Admin><AdminJDPrep /></Admin>} />
                <Route path="/admin/study-content"    element={<Admin><AdminStudyContent /></Admin>} />
                <Route path="/admin/explain-content" element={<Admin><AdminExplainContent /></Admin>} />
                <Route path="/admin/support"          element={<Admin><AdminSupport /></Admin>} />
                <Route path="/admin/blog"             element={<Admin><AdminBlog /></Admin>} />
                <Route path="/admin/carousel"          element={<Admin><AdminCarousel /></Admin>} />
                <Route path="/admin/companies"          element={<Admin><AdminCompanies /></Admin>} />
                <Route path="/admin/companies/:id"     element={<Admin><AdminCompanies /></Admin>} />
                <Route path="/admin/company-coupons"   element={<Admin><AdminCompanyCoupons /></Admin>} />
                <Route path="/admin/courses"           element={<Admin><AdminCourses /></Admin>} />
                <Route path="/admin/courses/generate"  element={<Admin><AdminCourseGenerate /></Admin>} />
                <Route path="/admin/courses/:id"       element={<Admin><AdminCourseDetail /></Admin>} />
                <Route path="/admin/vouchers"          element={<Admin><AdminVouchers /></Admin>} />
                <Route path="/admin/withdrawals"       element={<Admin><AdminWithdrawals /></Admin>} />
                <Route path="/admin/company-reviews"   element={<Admin><AdminCompanyReviews /></Admin>} />
                <Route path="/admin/docs"              element={<Admin><AdminDocsPage /></Admin>} />

                {/* Company portal — public */}
                <Route path="/company/login"    element={<CompanyLoginPage />} />
                <Route path="/company/register" element={<CompanyRegisterPage />} />
                <Route path="/company/activate" element={<CompanyActivatePage />} />
                <Route path="/company/pricing"  element={<CompanyPricingPage />} />

                {/* Company portal — protected (sidebar layout) */}
                <Route path="/company/dashboard"                  element={<Company><CompanyDashboard /></Company>} />
                <Route path="/company/assessments"                element={<Company><AssessmentsListPage /></Company>} />
                <Route path="/company/assessments/new"            element={<Company><AssessmentCreatePage /></Company>} />
                <Route path="/company/assessments/:id"            element={<Company><AssessmentDetailPage /></Company>} />
                <Route path="/company/assessments/:id/candidates" element={<Company><CandidatesPage /></Company>} />
                <Route path="/company/assessments/:id/candidates/:sessionId" element={<Company><CandidateSessionPage /></Company>} />
                <Route path="/company/team"                       element={<Company><TeamPage /></Company>} />
                <Route path="/company/credits"                    element={<Company><CreditsPage /></Company>} />
                <Route path="/company/billing"                    element={<Company><BillingPage /></Company>} />
                <Route path="/company/help"                       element={<Company><CompanyHelpPage /></Company>} />
                <Route path="/company/profile"                    element={<Company><CompanyProfilePage /></Company>} />
                <Route path="/company/cv-scan"                    element={<Company><CVScanPage /></Company>} />
                <Route path="/company/candidates"                 element={<Company><TalentPoolPage /></Company>} />
                <Route path="/company/skills-tests"              element={<Company><SkillsTestsPage /></Company>} />
                <Route path="/company/white-label"               element={<Company><WhitelabelPage /></Company>} />

                {/* Recruit module */}
                <Route path="/company/recruit"                         element={<Company><RecruitPage /></Company>} />
                <Route path="/company/recruit/jobs/new"                element={<Company><PostJobPage /></Company>} />
                <Route path="/company/recruit/jobs/:jobId"             element={<Company><JobDetailPage /></Company>} />
                <Route path="/company/recruit/jobs/:jobId/edit"        element={<Company><PostJobPage /></Company>} />

                {/* New B2B pages */}
                <Route path="/company/analytics"         element={<Company><AnalyticsDashboardPage /></Company>} />
                <Route path="/company/audit-log"         element={<Company><AuditLogPage /></Company>} />
                <Route path="/company/departments"       element={<Company><DepartmentsPage /></Company>} />
                <Route path="/company/hiring-requests"   element={<Company><HiringRequestsPage /></Company>} />
                <Route path="/company/email-templates"       element={<Company><EmailTemplatesPage /></Company>} />
                <Route path="/company/automation"            element={<Company><AutomationPage /></Company>} />
                <Route path="/company/pipeline-templates"    element={<Company><PipelineTemplatesPage /></Company>} />
                <Route path="/company/webhooks"              element={<Company><WebhooksPage /></Company>} />
                <Route path="/company/talent-community"  element={<Company><TalentCommunityAdminPage /></Company>} />
                <Route path="/company/gdpr"             element={<Company><GdprPage /></Company>} />
                <Route path="/company/brand-analytics"  element={<Company><BrandAnalyticsPage /></Company>} />
                <Route path="/company/sla"              element={<Company><SlaPage /></Company>} />
                <Route path="/company/diversity"        element={<Company><DiversityPage /></Company>} />
                <Route path="/company/widget"           element={<Company><WidgetPage /></Company>} />
                <Route path="/company/re-engage"        element={<Company><ReEngagePage /></Company>} />
                <Route path="/company/interviews"        element={<Company><InterviewsPage /></Company>} />
                <Route path="/company/interview/:roomId" element={<VideoInterviewRoom />} />
                <Route path="/company/post-hire"         element={<Company><PostHirePage /></Company>} />
                <Route path="/interview/:token"          element={<CandidateVideoRoom />} />
                <Route path="/reference/:token"          element={<ReferencePage />} />
                <Route path="/talent/:companySlug"       element={<TalentCommunityPage />} />
                <Route path="/nps/:token"               element={<NpsPage />} />
                <Route path="/schedule/:token"          element={<InterviewSchedulePage />} />
                <Route path="/portal"                   element={<CandidatePortalPage />} />
                <Route path="/report/:token"            element={<FeedbackReportPage />} />

                {/* Careers index */}
                <Route path="/careers" element={<CareersIndexPage />} />

                {/* Internal job board (employee link) */}
                <Route path="/internal/:token"              element={<InternalJobsPage />} />

                {/* Public job apply + candidate tracking */}
                <Route path="/apply/:companySlug/:jobSlug"  element={<JobApplyPage />} />
                <Route path="/track/:token"                 element={<ApplicationTrackPage />} />
                <Route path="/review/:token"                element={<JobReviewPage />} />

                {/* Candidate assessment (public) */}
                <Route path="/assess/:token"    element={<CandidateAssessmentPage />} />

                {/* Company careers pages — catch-all slugs (MUST be last before 404) */}
                <Route path="/:companySlug/jobs/:jobSlug" element={<CompanyJobPage />} />
                <Route path="/:companySlug"               element={<CompanyCareersPage />} />

                {/* 404 */}
                <Route path="*" element={
                  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg,#f5f4f0)' }}>
                    <div style={{ textAlign:'center' }}>
                      <p style={{ fontSize:64, fontWeight:800, color:'#e8e6df', margin:'0 0 8px' }}>404</p>
                      <a href="/" style={{ color:'#1B3A5C', fontSize:14 }}>← Back home</a>
                    </div>
                  </div>
                } />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </CompanyAuthProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}
